import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, List } from '@mui/material'
import ParticipantListControls from 'src/participantsHome/ParticipantListControls'
import PriorityFilters from 'src/participantsHome/PriorityFilters'
import FiltersMenu from 'src/participantsHome/FiltersMenu'
import { participantMatchesPriorityFilters } from 'src/participantsHome/priorityFilterUtils'
import useParticipantFilters from 'src/participantsHome/useParticipantFilters'
import { useSessionUID } from 'src/session/useSession'
import { useStalledAfterDays } from '../api/AdvancedSettingsProvider'
import { AdvancedPatientSummary } from '../api/types'
import { compareCaseloadAttention } from '../api/selectors'
import AdvancedCaseloadGroups from './AdvancedCaseloadGroups'
import AdvancedCaseloadListItem from './AdvancedCaseloadListItem'
import { filterBarSx, filterBarViewSx, filterControlsSx } from './caseloadChrome'
import CaseloadIndicatorFilters from './CaseloadIndicatorFilters'
import { caseloadFilters } from './caseloadIndicators'
import { useCaseloadModules, useCollapsedThemes } from './caseloadModules'
import { useCaseloadView } from './caseloadView'
import CaseloadViewSwitch from './CaseloadViewSwitch'
import { useAdvancedCaseloadParticipants } from './useAdvancedCaseloadParticipants'

type Props = {
  patients: AdvancedPatientSummary[]
  selectedUid?: string
}

const AdvancedCaseloadList = ({ patients, selectedUid }: Props) => {
  const uids = useMemo(() => patients.map((patient) => patient.uid), [patients])
  const participants = useAdvancedCaseloadParticipants(uids)
  const participantList = useMemo(() => Object.values(participants), [participants])
  const [view, setView] = useCaseloadView()
  const coachID = useSessionUID()
  const stalledAfterDays = useStalledAfterDays()

  // The role reads server's care team, as on Standard: primary there, or else another member (ADR-129).
  const {
    availableFilterTags,
    filteredParticipants,
    filters,
    isFiltered,
    participantsInScope,
    priorityCounts,
    uidsWithDraftMessage,
    updateFilters,
  } = useParticipantFilters(participantList, selectedUid, { matchAnyTag: true, coachID })
  const history = useHistory()
  const [indicatorFilter, setIndicatorFilter] = useState<string | null>(null)
  // Held here, not per theme, so a view switch or a re-read keeps what the coach collapsed.
  const [collapsedThemes, toggleTheme] = useCollapsedThemes()
  const indicatorFilters = useMemo(() => caseloadFilters(view), [view])
  const activeIndicatorFilter = indicatorFilters.find((filter) => filter.key === indicatorFilter)

  const sorted = useMemo(() => {
    const labelFor = (patient: AdvancedPatientSummary) =>
      (participants[patient.uid]?.displayName || patient.uid).toLocaleLowerCase()

    return [...patients].sort(
      (a, b) => compareCaseloadAttention(a, b, stalledAfterDays) || labelFor(a).localeCompare(labelFor(b))
    )
  }, [patients, participants, stalledAfterDays])

  // The Nutritionist view hides the other priority chips, so clear their filters.
  useEffect(() => {
    const kept = filters.priorities.filter((priority) => priority === 'safety' || priority === 'waiting')
    if (view === 'nutritionist' && kept.length !== filters.priorities.length) {
      updateFilters({ priorities: kept })
    }
  }, [view, filters.priorities, updateFilters])

  useEffect(() => {
    if (indicatorFilter && !activeIndicatorFilter) {
      setIndicatorFilter(null)
    }
  }, [indicatorFilter, activeIndicatorFilter])

  // Like Standard's pills, these count within the tag and role filters; rows without a `server` name count only unfiltered.
  const indicatorCounts = useMemo(() => {
    const scoped = new Set(participantsInScope.map((participant) => participant.uid))
    const inScope = patients.filter(
      (summary) => scoped.has(summary.uid) || (filters.tags.length === 0 && filters.roles.length === 0)
    )

    return Object.fromEntries(
      indicatorFilters.map((filter) => [
        filter.key,
        inScope.filter((summary) => filter.matches({ summary, participant: participants[summary.uid] })).length,
      ])
    )
  }, [patients, participants, participantsInScope, filters.tags, filters.roles, indicatorFilters])

  const selectedSummary = patients.find((summary) => summary.uid === selectedUid)
  const selectedParticipant = selectedUid ? participants[selectedUid] : undefined

  // As on Standard, selecting a pill the open patient doesn't match closes it; one that stops matching while open stays.
  const closeSelectedUnless = (matches: boolean) => {
    if (selectedUid && !matches) {
      history.replace('/advanced')
    }
  }

  // One pill at a time across Standard's priorities and these, as on Standard's own row.
  const selectPriorities = (priorities: typeof filters.priorities) => {
    updateFilters({ priorities })
    if (priorities.length > 0) {
      setIndicatorFilter(null)
      closeSelectedUnless(
        selectedParticipant !== undefined &&
          participantMatchesPriorityFilters(
            selectedParticipant,
            uidsWithDraftMessage.has(selectedParticipant.uid),
            priorities
          )
      )
    }
  }

  const selectIndicatorFilter = (key: string | null) => {
    setIndicatorFilter(key)
    const filter = indicatorFilters.find((candidate) => candidate.key === key)
    if (filter) {
      updateFilters({ priorities: [] })
      closeSelectedUnless(
        selectedSummary !== undefined && filter.matches({ summary: selectedSummary, participant: selectedParticipant })
      )
    }
  }

  const visibleUids = new Set(filteredParticipants.map((participant) => participant.uid))
  const visible = (isFiltered ? sorted.filter((summary) => visibleUids.has(summary.uid)) : sorted).filter(
    (summary) =>
      !activeIndicatorFilter ||
      summary.uid === selectedUid ||
      activeIndicatorFilter.matches({ summary, participant: participants[summary.uid] })
  )
  const modules = useCaseloadModules(uids, visible, view === 'therapist')

  const renderRow = (summary: AdvancedPatientSummary) => (
    <AdvancedCaseloadListItem
      key={summary.uid}
      summary={summary}
      participant={participants[summary.uid]}
      view={view}
      selected={summary.uid === selectedUid}
    />
  )

  return (
    <>
      <Box sx={[filterBarSx, filterBarViewSx(view)]}>
        <FiltersMenu
          tags={availableFilterTags}
          selectedTags={filters.tags}
          selectedRoles={filters.roles}
          onTagsChange={(tags) => updateFilters({ tags })}
          onRolesChange={(roles) => updateFilters({ roles })}
          onClear={() => updateFilters({ roles: [], tags: [] })}
          controls={
            <Box sx={filterControlsSx}>
              <ParticipantListControls />
            </Box>
          }
          priorityFilters={
            <PriorityFilters counts={priorityCounts} selectedFilters={filters.priorities} onChange={selectPriorities} />
          }
        />
        <CaseloadIndicatorFilters
          filters={indicatorFilters}
          counts={indicatorCounts}
          selected={activeIndicatorFilter?.key ?? null}
          onChange={selectIndicatorFilter}
        />
        <CaseloadViewSwitch view={view} onChange={setView} />
      </Box>

      {modules ? (
        <AdvancedCaseloadGroups
          modules={modules}
          collapsed={collapsedThemes}
          onToggle={toggleTheme}
          renderRow={renderRow}
        />
      ) : (
        <List disablePadding>{visible.map(renderRow)}</List>
      )}
    </>
  )
}

export default AdvancedCaseloadList
